// A reply the page reload cut off.
//
// The chat saves its thread to sessionStorage 500 ms after each change, and
// the server keeps answering after the page goes away. A reload a second or
// two after Send came back to the coach's question with no reply, no spinner
// and no note, while the server had finished the answer. The next message
// ("did that finish?") then got an unrelated reply: the server's transcript
// held the answer the coach never saw (bigtest, 2026-10-07).
//
// So a turn is marked as pending before the request goes out and unmarked
// when it ends. A marker still there after a reload is a turn the coach never
// saw finish: the thread gets the coach's message back (if the 500 ms save
// missed it) and a notice, and the next request tells the server, which tells
// the model its last reply never arrived.

export const PENDING_TURN_STORAGE_KEY = 'mealops:assistant-pending-turn:v1';
const PENDING_TURN_MAX_AGE_MS = 24 * 60 * 60 * 1000;

export const INTERRUPTED_TURN_NOTICE =
  'The page reloaded before this reply arrived. The assistant may have finished it anyway (check your cart): ask "what did you find?" to see the reply, or send your request again.';

const storageOf = (storage) => {
  if (storage) return storage;
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
};

export function writePendingTurn({ userId, conversationId, text, sentAt = Date.now() }, storage = null) {
  try {
    storageOf(storage)?.setItem(PENDING_TURN_STORAGE_KEY, JSON.stringify({ userId, conversationId, text, sentAt }));
  } catch {
    // Quota/privacy-mode failures just mean no recovery note.
  }
}

export function clearPendingTurn(storage = null) {
  try {
    storageOf(storage)?.removeItem(PENDING_TURN_STORAGE_KEY);
  } catch {
    // Ignore.
  }
}

/** This user's pending turn, or null (none, another user's, or too old). */
export function readPendingTurn(userId, { storage = null, now = Date.now() } = {}) {
  try {
    const raw = storageOf(storage)?.getItem(PENDING_TURN_STORAGE_KEY);
    if (!raw) return null;
    const pending = JSON.parse(raw);
    if (!pending || pending.userId !== userId || typeof pending.text !== 'string') return null;
    if (!pending.sentAt || now - pending.sentAt > PENDING_TURN_MAX_AGE_MS) return null;
    return pending;
  } catch {
    return null;
  }
}

/**
 * The restored thread and text history with the cut-off turn put back: the
 * coach's message when the save missed it, then the notice. `makeId` mints
 * thread entry ids.
 */
export function recoverInterruptedTurn({ thread = [], history = [], pending, makeId }) {
  if (!pending) return { thread, history };
  const text = pending.text.trim();
  const lastUser = [...thread].reverse().find((entry) => entry?.type === 'user');
  const nextThread = [...thread];
  const nextHistory = [...history];
  if (text && lastUser?.text !== text) {
    nextThread.push({ id: makeId(), type: 'user', text });
  }
  const lastHistory = nextHistory[nextHistory.length - 1];
  if (text && !(lastHistory?.role === 'user' && lastHistory.content === text)) {
    nextHistory.push({ role: 'user', content: text });
  }
  nextThread.push({ id: makeId(), type: 'status', label: INTERRUPTED_TURN_NOTICE, icon: 'Info' });
  return { thread: nextThread, history: nextHistory };
}
